import { useCollection } from "@amzn/awsui-collection-hooks";
import {
    Box,
    Button,
    Header,
    Link,
    Pagination,
    StatusIndicator,
    Table
} from "@amzn/awsui-components-react";
import * as React from "react";
import { KBSyncStatusItem } from "../../api/knowledge-base.api";
import { useAPI2 } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ErrorFlashbar } from "../common/ErrorFlashbar";

/**
 * Prototype page sync status table: the LAST sync outcome per page, straight from the
 * KnowledgeBaseSyncStatus table the sync Lambda writes. Pages that never synced (or synced
 * before this table existed) have no row - a Re-sync backfills them.
 * Paginated client-side so instances with many pages don't stretch the tab.
 */
const PAGE_SIZE = 10;

export const KnowledgeBaseSyncStatusTable = (props: {
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);
    const listRunner = useAPI2(context.getKnowledgeBaseAPI().listKBSyncStatus);

    React.useEffect(() => {
        listRunner.invoke(props.instanceId);
    }, [props.instanceId]);

    const statuses = listRunner.output?.statuses ?? [];
    const { items, paginationProps } = useCollection(statuses, {
        pagination: { pageSize: PAGE_SIZE }
    });

    return (
        <>
            {listRunner.status === "Error" && <ErrorFlashbar error={listRunner.err} />}
            <Table
                header={
                    <Header
                        variant="h3"
                        counter={statuses.length > 0 ? `(${statuses.length})` : undefined}
                        description="Last sync outcome per page. Pages appear here after their first sync - use Re-sync pages to backfill."
                        actions={
                            <Button
                                iconName="refresh"
                                ariaLabel="Refresh sync status"
                                loading={listRunner.status === "Running"}
                                onClick={() => listRunner.invoke(props.instanceId)}
                            />
                        }
                    >
                        Page sync status
                    </Header>
                }
                columnDefinitions={[
                    {
                        id: "page",
                        header: "Page",
                        cell: item => (
                            <Link href={`#/page/${item.id}/live`}>
                                {item.pageTitle ?? item.id}
                            </Link>
                        )
                    },
                    {
                        id: "action",
                        header: "Last sync",
                        cell: item => item.action === "INGESTED" ?
                            <StatusIndicator type="success">Ingested</StatusIndicator> :
                            <StatusIndicator type="stopped">Removed</StatusIndicator>
                    },
                    {
                        id: "version",
                        header: "Content version",
                        // Assembly pins containers to the live page's refs, so an ingested
                        // version is always a published one - drafts never reach the index.
                        cell: item => item.pageVersion != null ? `Published v${item.pageVersion}` : "-"
                    },
                    {
                        id: "documentStatus",
                        header: "Index status",
                        cell: item => item.documentStatus ?? "-"
                    },
                    {
                        id: "syncDate",
                        header: "Synced at",
                        cell: item => item.syncDate ? new Date(item.syncDate).toLocaleString() : "-"
                    }
                ]}
                items={items}
                pagination={statuses.length > PAGE_SIZE && <Pagination {...paginationProps} />}
                loading={listRunner.status === "Running" && !listRunner.output}
                loadingText="Loading sync status"
                empty={
                    <Box textAlign="center" color="text-status-inactive">
                        No pages have synced yet. Opt a page in to the Knowledge Base, or use
                        Re-sync pages.
                    </Box>
                }
                variant="embedded"
                trackBy="id"
            />
        </>
    );
};
